import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Upload, Download, Repeat, Loader2, Link2, Eye, EyeOff, Info } from "lucide-react";

const STATUS_LABEL = {
  submitted_application: "Submitted application",
  not_verified: "Not carrier-verified",
};

function planLabel(p) {
  if (!p) return null;
  const bits = [p.carrier, p.plan_name].filter(Boolean).join(" · ");
  return bits || p.display_id || (p.plan_year ? `Plan year ${p.plan_year}` : null);
}

function covLabel(cv) {
  const d = cv.descriptive || {};
  const vi = cv.verified_identity || {};
  const name = vi.plan_name || d.plan_name;
  const carrier = vi.carrier || d.carrier_org;
  const base = [carrier, name].filter(Boolean).join(" · ");
  return base || vi.display_id || `Coverage ${cv.id.slice(-5)}`;
}

export default function SunfireEnrollments({ hid }) {
  const [docs, setDocs] = useState(null);
  const [coverages, setCoverages] = useState([]);
  const [events, setEvents] = useState([]);
  const [link, setLink] = useState("");   // "" | cov:<id> | evt:<id>
  const [consumerVisible, setConsumerVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef();
  const replaceRef = useRef();
  const replaceTarget = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/agent/households/${hid}/sunfire-enrollments`);
    setDocs(data.documents);
  };
  const loadLinks = async () => {
    try { const { data } = await api.get(`/agent/households/${hid}`); setCoverages(data.coverages || []); }
    catch { setCoverages([]); }
    try { const { data } = await api.get(`/agent/households/${hid}/enrollment`); setEvents(data.events || []); }
    catch { setEvents([]); }
  };
  useEffect(() => { load(); loadLinks(); /* eslint-disable-next-line */ }, [hid]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (link.startsWith("cov:")) fd.append("coverage_id", link.slice(4));
      else if (link.startsWith("evt:")) fd.append("enrollment_event_id", link.slice(4));
      fd.append("consumer_visible", consumerVisible ? "true" : "false");
      await api.post(`/agent/households/${hid}/sunfire-enrollments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const view = async (d) => {
    const res = await api.get(`/agent/sunfire-enrollments/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const replace = async (e) => {
    const f = e.target.files?.[0];
    if (!f || !replaceTarget.current) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/agent/sunfire-enrollments/${replaceTarget.current}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e2) { setErr(e2?.response?.data?.detail || "Replace failed"); }
    finally { setBusy(false); replaceTarget.current = null; if (replaceRef.current) replaceRef.current.value = ""; }
  };

  const toggleShare = async (d) => {
    setBusy(true);
    try { await api.patch(`/agent/sunfire-enrollments/${d.id}`, { consumer_visible: !d.consumer_visible }); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="sunfire-enrollments">
      <input ref={replaceRef} type="file" accept="application/pdf,.pdf" onChange={replace} className="hidden" data-testid="sunfire-replace-input" />
      <div className="rounded-xl border-2 border-[#CFE0F5] bg-[#F3F8FF] p-4">
        <div className="flex items-center gap-2 text-[#1E3E62] font-bold text-sm"><FileText className="w-4 h-4" /> Sunfire enrollment records</div>
        <p className="text-xs text-[#4B5563] mt-1 flex items-start gap-1">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          A submitted Sunfire application — not proof of carrier acceptance, effective coverage, agent-of-record, or a change to existing active coverage. Plan details are never read from the PDF.
        </p>
        <div className="mt-3 grid gap-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-[#6B7280]">Link to a policy or enrollment (optional)</label>
            <select value={link} onChange={(e) => setLink(e.target.value)} className="mt-1 h-10 w-full rounded-md border bg-white px-2 text-sm" data-testid="sunfire-link-select">
              <option value="">Not linked yet (pending association)</option>
              {coverages.length > 0 && (
                <optgroup label="Coverage records">
                  {coverages.map((cv) => <option key={cv.id} value={`cov:${cv.id}`}>{covLabel(cv)}</option>)}
                </optgroup>
              )}
              {events.length > 0 && (
                <optgroup label="Enrollment events">
                  {events.map((ev) => <option key={ev.id} value={`evt:${ev.id}`}>{(ev.label || ev.event_type)}{ev.plan ? ` · ${ev.plan.plan_name || ev.plan.display_id}` : ""}</option>)}
                </optgroup>
              )}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#0B192C]" data-testid="sunfire-consumer-visible">
            <input type="checkbox" checked={consumerVisible} onChange={(e) => setConsumerVisible(e.target.checked)} />
            Share with the client in their member portal
          </label>
          <input ref={fileRef} type="file" accept="application/pdf,.pdf" onChange={upload} className="hidden" data-testid="sunfire-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="h-10 bg-[#0B192C] text-white w-fit" data-testid="sunfire-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload Sunfire PDF
          </Button>
          <p className="text-xs text-[#9CA3AF]">PDF only · max 25MB · stored privately, visible to assigned staff (and the client only if you share).</p>
          {err && <p className="text-rose-600 text-sm" data-testid="sunfire-error">{err}</p>}
        </div>
      </div>

      {!docs ? <Skeleton className="h-28 w-full rounded-xl" /> : docs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-[#E5E5E0] bg-white p-6 text-center text-sm text-[#6B7280]" data-testid="sunfire-empty">No Sunfire enrollment records yet.</div>
      ) : (
        <div className="rounded-xl border-2 border-[#E5E5E0] bg-white divide-y" data-testid="sunfire-list">
          {docs.map((d) => (
            <div key={d.id} className="p-3" data-testid={`sunfire-doc-${d.id}`}>
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#0B192C] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#0B192C] truncate">{d.original_filename}</p>
                  <p className="text-xs text-[#6B7280]">
                    {d.association_state === "linked" ? <span className="inline-flex items-center gap-1"><Link2 className="w-3 h-3" />{planLabel(d.plan) || "Linked policy"}</span> : "Pending association"}
                    {d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ""} · {(d.uploaded_at || "").slice(0, 10)}
                  </p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => view(d)} data-testid={`sunfire-view-${d.id}`}><Download className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title="Replace with a corrected PDF" onClick={() => { replaceTarget.current = d.id; replaceRef.current?.click(); }} data-testid={`sunfire-replace-${d.id}`}><Repeat className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title={d.consumer_visible ? "Shared with client — click to unshare" : "Private — click to share with client"} onClick={() => toggleShare(d)} data-testid={`sunfire-share-${d.id}`}>
                  {d.consumer_visible ? <Eye className="w-4 h-4 text-[#2D6A4F]" /> : <EyeOff className="w-4 h-4 text-[#9CA3AF]" />}
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2 pl-8">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">{STATUS_LABEL[d.submission_status] || d.submission_status}</span>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#EEF2F6] text-[#1E3E62]">{STATUS_LABEL[d.effective_status] || d.effective_status}</span>
                {d.writing_agent?.name && <span className="text-xs px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#374151]">Writing agent: {d.writing_agent.name}</span>}
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#374151]">{d.writing_agency}</span>
                {d.consumer_visible && <span className="text-xs px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">Shared with client</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
